import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { theme, surface, text, ACCENT } from "./theme";
import { useFeeds } from "./hooks/useFeeds";
import { useArticles } from "./hooks/useArticles";
import { useDebouncedValue } from "./hooks/useDebouncedValue";
import { usePersistentState } from "./hooks/usePersistentState";
import { useEmbedBridge } from "./hooks/useEmbedBridge";
import { Header } from "./components/Header";
import { ControlBar } from "./components/ControlBar";
import { ScrollTopButton } from "./components/ScrollTopButton";
import { ArticleGrid } from "./components/ArticleGrid";
import { ArticleSkeletonGrid } from "./components/ArticleSkeleton";
import {
  ErrorState,
  NoArticlesState,
  NoResultsState,
  NoSourcesState,
} from "./components/StateViews";

const STORAGE_KEY = "tezlens.selectedFeedIds";

function App() {
  const bridge = useEmbedBridge();
  const { embedded } = bridge;
  const { feeds, isLoading: feedsLoading, error: feedsError, reload } = useFeeds();

  const [storedFeedIds, setStoredFeedIds] = usePersistentState<string[] | null>(
    STORAGE_KEY,
    null
  );
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim(), 300);

  /**
   * Default to every source. Stored selections are intersected with the live
   * feed list so a removed feed cannot linger, and newly added feeds show up
   * for readers who never customised their selection.
   */
  const selectedFeedIds = useMemo(() => {
    if (feeds.length === 0) return [];
    if (storedFeedIds == null) return feeds.map((feed) => feed.id);
    const known = new Set(feeds.map((feed) => feed.id));
    return storedFeedIds.filter((id) => known.has(id));
  }, [feeds, storedFeedIds]);

  const {
    articles,
    total,
    hasMore,
    isLoading,
    isLoadingMore,
    isRefreshing,
    error,
    lastUpdated,
    loadMore,
    refresh,
  } = useArticles({
    feedIds: selectedFeedIds,
    ready: feeds.length > 0,
    search,
  });

  const selectAll = useCallback(
    () => setStoredFeedIds(feeds.map((feed) => feed.id)),
    [feeds, setStoredFeedIds]
  );

  const showSkeletons = (feedsLoading || isLoading) && articles.length === 0;
  const noSources = feeds.length > 0 && selectedFeedIds.length === 0;
  const isEmpty =
    !showSkeletons && !error && !noSources && articles.length === 0;
  const noResults = isEmpty && !!search;
  // Selected sources are valid but have published nothing (e.g. a single
  // source that is currently down).
  const noArticles = isEmpty && !search && feeds.length > 0;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          bgcolor: "background.default",
          minHeight: embedded ? 0 : "100vh",
          py: { xs: 2.5, md: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Header total={total} lastUpdated={lastUpdated} />
        </Container>

        <ControlBar
          search={searchInput}
          onSearchChange={setSearchInput}
          onRefresh={refresh}
          isRefreshing={isRefreshing}
          feeds={feeds}
          selected={selectedFeedIds}
          onSelectionChange={setStoredFeedIds}
          bridge={bridge}
        />

        <Container maxWidth="xl">
          {feedsError && !feeds.length ? (
            <Box sx={{ display: "grid" }}>
              <ErrorState message={feedsError} onRetry={reload} />
            </Box>
          ) : error && articles.length === 0 ? (
            <Box sx={{ display: "grid" }}>
              <ErrorState message={error} onRetry={refresh} />
            </Box>
          ) : noSources ? (
            <Box sx={{ display: "grid" }}>
              <NoSourcesState onSelectAll={selectAll} />
            </Box>
          ) : noResults ? (
            <Box sx={{ display: "grid" }}>
              <NoResultsState search={search} onClear={() => setSearchInput("")} />
            </Box>
          ) : noArticles ? (
            <Box sx={{ display: "grid" }}>
              <NoArticlesState onSelectAll={selectAll} />
            </Box>
          ) : showSkeletons ? (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
              }}
            >
              <ArticleSkeletonGrid count={9} />
            </Box>
          ) : (
            <ArticleGrid articles={articles} feeds={feeds} grouped={!search} />
          )}

          {hasMore && articles.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
              <Button
                onClick={loadMore}
                disabled={isLoadingMore}
                variant="outlined"
                sx={{
                  px: 4,
                  py: 1,
                  color: ACCENT,
                  borderColor: surface.borderStrong,
                  "&:hover": { borderColor: ACCENT, backgroundColor: surface.raised },
                }}
              >
                {isLoadingMore
                  ? "Loading…"
                  : `Load more (${(total - articles.length).toLocaleString()} left)`}
              </Button>
            </Box>
          )}

          {!hasMore && articles.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                color: text.tertiary,
                pt: 4,
              }}
            >
              That's everything — {articles.length.toLocaleString()} stor
              {articles.length === 1 ? "y" : "ies"}.
            </Typography>
          )}
        </Container>

        <ScrollTopButton bridge={bridge} />
      </Box>
    </ThemeProvider>
  );
}

export default App;
